import { useCallback, useState } from 'react';
import { Input } from '../../atoms/input';
import { FormField } from '@design-system/molecules/form';
import { useFormContext, useFormState } from 'react-hook-form';
import { EmailFieldProps } from '@design-system/molecules/email-field/email-field.types';

/**
 * EmailField component
 * @param props - EmailFieldProps
 * @returns EmailField component
 */
export const EmailField = (props: EmailFieldProps) => {
  const {
    name,
    label,
    placeholder,
    hintConfig: { useErrorAsHint = false, hint },
    ...inputProps
  } = props;

  const { errors } = useFormState();
  const { watch } = useFormContext();
  const [isFocused, setIsFocused] = useState(false);

  const getHint = useCallback(() => {
    const fieldValue = watch(name);

    if (!isFocused) {
      return undefined;
    }

    /**
     * Show hint when user has entered what looks
     * like a domain (e.g. "example.com") without the @ symbol
     */
    if (fieldValue && !fieldValue?.includes('@') && /\.\w{2,}$/.test(fieldValue)) {
      return hint;
    }

    if (useErrorAsHint && errors[name]?.message) {
      return errors[name]?.message;
    }
  }, [useErrorAsHint, hint, errors, name, watch, isFocused]);

  return (
    <FormField name={name}>
      {({ ...field }) => {
        return (
          <Input
            {...field}
            {...inputProps}
            label={label}
            type="email"
            placeholder={placeholder}
            // @ts-expect-error
            hint={getHint()}
            error={errors[name]?.message?.toString()}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        );
      }}
    </FormField>
  );
};

EmailField.displayName = 'EmailField';
